import React, { memo, ReactNode } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'

interface Props {
  title: string
  /** Plain value; ignored when `component` is given. */
  text?: string | number | null
  /** Rich value — a chip, a link — rendered in place of `text`. */
  component?: ReactNode
  /** A control that belongs to the row, pinned to its right edge: a copy or edit button. */
  action?: ReactNode
  /**
   * `row` (default) puts the value on the right, opposite the label. `column` stacks the value
   * under the label, both flush left — for long values such as ids.
   */
  direction?: 'row' | 'column'
  className?: any
  textClassName?: any
  dataTestId?: string
}

/**
 * The dashboard's label/value row (`components/shared/DataRow`), trimmed to what this app
 * needs: grey `h6` label on the left, black `body2` value on the right, and a hairline under
 * each row so a stack of them reads as one separated list — the layout every detail panel in
 * the dashboard uses. No inline editing here; rows that need it get their own control via
 * `action`.
 */
const DataRowComponent = ({
  title,
  text,
  component,
  action,
  direction = 'row',
  className,
  textClassName,
  dataTestId,
}: Props) => {
  return (
    <div
      css={[
        tw`flex flex-row items-center justify-between gap-4 border-0 border-b border-solid border-shades-100 py-3`,
        className,
      ]}
      {...(dataTestId ? { 'data-testid': dataTestId } : {})}
    >
      <div
        css={[
          tw`flex min-w-0 flex-1 gap-2`,
          direction === 'row'
            ? tw`flex-row items-center justify-between gap-4`
            : tw`flex-col items-start gap-1`,
        ]}
      >
        <Typography variant="h6" css={tw`shrink-0 text-shades-400`}>
          {title}
        </Typography>
        {component ?? (
          <Typography
            variant="body2"
            css={[
              tw`break-words text-shades-1000`,
              direction === 'row' ? tw`text-end` : tw`text-start`,
              textClassName,
            ]}
          >
            {text}
          </Typography>
        )}
      </div>
      {action ? <div css={tw`flex shrink-0 items-center`}>{action}</div> : null}
    </div>
  )
}

export const DataRow = memo(DataRowComponent)
